










































import {
  computed,
  defineComponent,
  ref,
  useContext,
  useFetch,
  useRoute,
} from '@nuxtjs/composition-api';
import { SfBreadcrumbs, SfLoader } from '@storefront-ui/vue';
import { merge } from 'lodash-es';

import LoadWhenVisible from '~/components/utils/LoadWhenVisible.vue';
import { useApi } from '~/composables';
import { getMetaInfo } from '~/helpers/getMetaInfo';
import { useI18n } from '~/helpers/hooks/usei18n';
import ProductSkeleton from '~/modules/catalog/product/components/ProductSkeleton.vue';
import { useProduct } from '~/modules/catalog/product/composables/useProduct';
import { useRecentProducts } from '~/modules/catalog/product/composables/useRecentProducts';
import { ProductTypeEnum } from '~/modules/catalog/product/enums/ProductTypeEnum';
import getProductPriceBySkuGql from '~/modules/catalog/product/queries/getProductPriceBySku.gql';
import type { Product } from '~/modules/catalog/product/types';
import type { ProductDetailsQuery } from '~/modules/GraphQL/types';

export default defineComponent({
  name: 'ProductPage',
  components: {
    ProductSkeleton,
    SimpleProduct: () =>
      import(
        '~/modules/catalog/product/components/product-types/simple/SimpleProduct.vue'
      ),
    RecentProducts: () =>
      import(/* webpackPrefetch: true */ '~/components/RecentProducts.vue'),
    LoadWhenVisible,
    SfBreadcrumbs,
    SfLoader,
    Reviews: () =>
      import(/* webpackPrefetch: true */ '~/components/Reviews.vue'),
  },
  setup() {
    const { query } = useApi();
    const product = ref<Product | null>(null);
    const { localePath } = useContext();
    const route = useRoute();
    const { locale } = useI18n();
    const { path } = route.value;
    const productreviewsrating = ref<any>([]);
    const clearUrl = path
      .replace(/[a-z]+\/[cp|]\//gi, '')
      .replace(`/${locale}`, '')
      .replace('/product', '')
      .replace(/^\/|\.html$/g, '');

    console.log('clearUrl product url key', clearUrl);

    const { getProductDetails } = useProduct();
    const isLoading = ref<boolean>(true);
    const { error: nuxtError, app } = useContext();
    const {
      load: loadRecentProduct,
      addItem: addItemToRecentProduct,
      removeItem: removeRecentProduct,
    } = useRecentProducts();

    const recentProducts = ref<Product[]>([]);
    const relatedProducts = computed<Product[]>(
      () => (product.value?.related_products as Product[]) ?? []
    );
    const breadcrumbs = ref([
      {
        text: 'Home',
        link: localePath('/'),
      },
    ]);
    // remove: in case of hydration error
    // const breadcrumbs = computed(() => {
    //   // if (!product.value) return [];
    //   // const productCategories = product.value?.categories ?? [];
    //   // const crumbs = getBreadcrumbs(
    //   //   product.value,
    //   //   Array.isArray(productCategories) ? [...productCategories].pop() : null
    //   // ).map((breadcrumb) => ({
    //   //   ...breadcrumb,
    //   //   link: localePath(breadcrumb.link),
    //   // }));
    //   const homebreadCrumbs = {
    //     text: 'Home',
    //     link: localePath('/'),
    //   };
    //   // Create a new array with homebreadCrumbs added to the beginning
    //   // return [homebreadCrumbs, ...crumbs];
    //   return [homebreadCrumbs];
    // });

    const addRecentProduct = async (product) => {
      await addItemToRecentProduct({ product });
    };
    const getBaseSearchQuery = () => ({
      filter: {
        url_key: { eq: clearUrl },
      },
    });
    const renderer = ProductTypeEnum.SIMPLE_PRODUCT;
    const handleProductGooleAnalytics = (product: Product) => {
      const ecommerce = {
        currency: product?.price_range?.minimum_price?.final_price?.currency,
        value: product?.price_range?.minimum_price?.final_price?.value,
        sku: product?.sku,
        items: [
          {
            item_id: product?.sku,
            item_name: product?.name,
            discount: product?.price_range?.minimum_price?.discount?.amount_off,
            index: product?.uid,
            price: product?.price_range?.minimum_price?.final_price?.value,
            quantity: 1,
          },
        ],
      };
      if (process.client) {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        window &&
          window?.dataLayer &&
          window?.dataLayer.push({
            event: 'view_item',
            ...{ ecommerce },
          });
      }
    };
    const fetchProductBaseData = async (searchQuery = getBaseSearchQuery()) => {
      const result = await getProductDetails({
        ...searchQuery,
      });

      console.log('=====> product base data:', result);

      if (result?.items?.length === 0) return;

      product.value = merge(
        {},
        product.value,
        (result.items[0] as Product) ?? null
      );

      if (locale === 'gb') {
        const response = await fetch(
          `https://api.feefo.com/api/20/products/ratings?merchant_identifier=unineed-limited&product_sku=${product.value.sku}`
        );

        if (response) {
          const ratingsData = await response.json();
          productreviewsrating.value = ratingsData?.products || [];
        }
      }

      isLoading.value = false;
    };

    const fetchProductExtendedData = async (
      searchQuery = getBaseSearchQuery()
    ) => {
      const { data } = await query<ProductDetailsQuery>(
        getProductPriceBySkuGql,
        searchQuery
      );
      product.value = merge(
        {},
        product.value,
        data.products?.items?.[0] as Product
      );
    };
    const loadRecentProducts = async () => {
      const result: any = await loadRecentProduct();
      recentProducts.value = result;
    };

    const removeRecentProductItem = async (product: Product) => {
      const result: any = await removeRecentProduct({ product });
      recentProducts.value = result;
    };

    useFetch(async () => {
      await fetchProductBaseData();

      if (Boolean(product?.value?.sku) === false) {
        if (process.client && window) {
          setTimeout(() => {
            nuxtError({
              statusCode: 404,
              message: app.i18n.t('OutOfStock') as string,
            });
          }, 500);
        }
        isLoading.value = false;
      } else {
        await fetchProductExtendedData();
        isLoading.value = false;
      }
      if (process.client && window) {
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        setTimeout(async () => {
          await loadRecentProducts();
          await addRecentProduct(product.value);
          handleProductGooleAnalytics(product.value);
        }, 300);
      }
    });
    return {
      renderer,
      isLoading,
      breadcrumbs,
      product,
      fetchProduct: fetchProductExtendedData,
      recentProducts,
      relatedProducts,
      removeRecentProductItem,
      productreviewsrating,
      locale,
    };
  },
  head() {
    return getMetaInfo(this.product);
  },
});
